import { ToolCall } from '../utils/types'
import { frontendToolRegistry } from './registry'
import { FrontendToolContext, FrontendToolResult, PendingFrontendToolCall } from './types'

/**
 * Execute a frontend tool call. If the tool requires confirmation, it is queued
 * into the pending list and a Promise is returned that resolves when the user
 * confirms or rejects. Otherwise executes immediately.
 */
export async function executeFrontendToolCall(
  toolCall: ToolCall,
  context: FrontendToolContext,
  pendingQueue: PendingFrontendToolCall[],
  setPendingQueue: (queue: PendingFrontendToolCall[]) => void
): Promise<FrontendToolResult> {
  const handler = frontendToolRegistry.get(toolCall.name)

  if (!handler) {
    return { success: false, message: `Unknown frontend tool: ${toolCall.name}` }
  }

  let parsedArgs: Record<string, any> = {}
  try {
    parsedArgs = JSON.parse(toolCall.arguments || '{}')
  } catch {
    return { success: false, message: `Failed to parse arguments for ${toolCall.name}` }
  }

  // Check abort before executing
  if (context.abortSignal?.aborted) {
    return { success: false, message: 'Aborted' }
  }

  if (handler.definition.requiresConfirmation) {
    // Queue for user approval — return a promise that the modal will resolve
    return new Promise<FrontendToolResult>((resolve) => {
      const pending: PendingFrontendToolCall = {
        toolCall,
        parsedArgs,
        handler,
        resolve
      }
      setPendingQueue([...pendingQueue, pending])
    })
  }

  // Execute immediately
  try {
    return await handler.execute(parsedArgs, context)
  } catch (err: any) {
    return { success: false, message: err?.message || `Error executing ${toolCall.name}` }
  }
}

/**
 * Called when the user confirms a pending frontend tool call.
 */
export async function confirmPendingToolCall(
  pending: PendingFrontendToolCall,
  context: FrontendToolContext
): Promise<void> {
  try {
    const result = await pending.handler.execute(pending.parsedArgs, context)
    pending.resolve(result)
  } catch (err: any) {
    pending.resolve({ success: false, message: err?.message || 'Execution failed' })
  }
}

/**
 * Called when the user rejects a pending frontend tool call.
 */
export function rejectPendingToolCall(pending: PendingFrontendToolCall): void {
  pending.resolve({
    success: false,
    message: `User declined action: ${pending.toolCall.name}`
  })
}
